import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../../../utils/utils';
import * as localforage from 'localforage';
import Swal from 'sweetalert2';

interface PDFReport {
    id: string;
    title: string;
    summary: string;
    patient_name: string;
    patient_id: string;
    report_type: string;
    created_date: string;
    pdf_url: string;
    file_size: string;
    attendee: string;
    status: string;
    department: string;
    download_count: number;
    is_available: boolean;
}

interface Props {
    onBack: () => void;
}

const ReportsCenter: React.FC<Props> = ({ onBack }) => {
    const baseUrl = useBaseUrl();
    const [activeTab, setActiveTab] = useState<string>('treatment');
    const [reports, setReports] = useState<PDFReport[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(12);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [totalCount, setTotalCount] = useState<number>(0);

    const reportTypes = [
        { key: 'treatment', name: 'Treatment', icon: 'bx-health', color: '#28a745' },
        { key: 'consultation', name: 'Consultation', icon: 'bx-user-voice', color: '#007bff' },
        { key: 'diagnostic', name: 'Diagnostic', icon: 'bx-test-tube', color: '#fd7e14' },
        { key: 'operative', name: 'Operative', icon: 'bx-plus-medical', color: '#dc3545' },
        { key: 'radiology', name: 'Radiology', icon: 'bx-x-ray', color: '#6610f2' },
        { key: 'pathology', name: 'Pathology', icon: 'bx-dna', color: '#e83e8c' },
        { key: 'progress', name: 'Progress Notes', icon: 'bx-note', color: '#20c997' },
        { key: 'discharge', name: 'Discharge', icon: 'bx-exit', color: '#6c757d' }
    ];

    useEffect(() => {
        loadReports();
    }, [activeTab]);

    const loadReports = async () => {
        setLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            if (!clinicId || !staffId) {
                Swal.fire('Error', 'Authentication required. Please log in again.', 'error');
                return;
            }

            // Only fetch PDF-converted reports from resources API
            const response = await axios.get(`${baseUrl}/resources/api/v1/library/?file_type=pdf&category__category_type=research&page=${currentPage}`, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                let pdfReports: PDFReport[] = [];
                
                if (Array.isArray(response.data.results)) {
                    // Paginated response
                    pdfReports = response.data.results
                        .filter((resource: any) => resource.file_type === 'pdf')
                        .map((resource: any) => ({
                            id: resource.id,
                            title: resource.title,
                            summary: resource.summary || resource.description,
                            patient_name: resource.metadata?.patient_name || 'N/A',
                            patient_id: resource.metadata?.patient_id || 'N/A',
                            report_type: activeTab,
                            created_date: resource.created_at,
                            pdf_url: resource.file_path,
                            file_size: resource.formatted_file_size,
                            attendee: resource.author,
                            status: resource.status,
                            department: resource.metadata?.department || 'General',
                            download_count: resource.download_count,
                            is_available: resource.can_download
                        }));
                    
                    setReports(currentPage === 1 ? pdfReports : [...reports, ...pdfReports]);
                    setTotalCount(response.data.count || 0);
                    setHasMore(!!response.data.next);
                } else if (Array.isArray(response.data)) {
                    // Simple array response
                    pdfReports = response.data
                        .filter((resource: any) => resource.file_type === 'pdf')
                        .map((resource: any) => ({
                            id: resource.id,
                            title: resource.title,
                            summary: resource.summary || resource.description,
                            patient_name: resource.metadata?.patient_name || 'N/A',
                            patient_id: resource.metadata?.patient_id || 'N/A',
                            report_type: activeTab,
                            created_date: resource.created_at,
                            pdf_url: resource.file_path,
                            file_size: resource.formatted_file_size,
                            attendee: resource.author,
                            status: resource.status,
                            department: resource.metadata?.department || 'General',
                            download_count: resource.download_count,
                            is_available: resource.can_download
                        }));
                    
                    setReports(pdfReports);
                    setTotalCount(pdfReports.length);
                    setHasMore(false);
                }
            }
        } catch (error) {
            console.error('Error loading PDF reports:', error);
            setReports([]); // No hardcoded data
        } finally {
            setLoading(false);
        }
    };

    const getAttendeeForReportType = (report: any) => {
        switch (activeTab) {
            case 'treatment':
                return report.attending_doctor || report.staff?.name || 'Dr. Smith';
            case 'consultation':
                return report.consulting_doctor || report.doctor?.name || 'Dr. Johnson';
            case 'radiology':
                return report.radiologist || report.staff?.name || 'Dr. Wilson';
            case 'pathology':
                return report.pathologist || report.staff?.name || 'Dr. Brown';
            case 'operative':
                return report.surgeon || report.staff?.name || 'Dr. Davis';
            default:
                return report.author || report.staff?.name || 'Dr. Anderson';
        }
    };

    const loadMoreReports = async () => {
        if (!hasMore || loading) return;
        
        setCurrentPage(prev => prev + 1);
        await loadReports();
    };

    const handleScroll = () => {
        if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight) return;
        loadMoreReports();
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasMore, loading]);

    const downloadReport = async (report: PDFReport) => {
        if (!report.is_available) {
            Swal.fire('Access Denied', 'This report is not available for download.', 'error');
            return;
        }

        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            const response = await axios.get(`${baseUrl}/resources/api/v1/library/${report.id}/download/`, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || ''
                },
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${report.title}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            Swal.fire('Success', 'Report downloaded successfully!', 'success');
        } catch (error) {
            console.error('Download error:', error);
            Swal.fire('Error', 'Failed to download report. Please try again.', 'error');
        }
    };

    const filteredReports = reports.filter(report =>
        report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.patient_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.attendee?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.department.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusBadge = (status: string) => {
        const statusColors = {
            completed: 'bg-success',
            pending: 'bg-warning',
            reviewed: 'bg-info',
            draft: 'bg-secondary'
        };
        return statusColors[status as keyof typeof statusColors] || 'bg-secondary';
    };

    const currentReportType = reportTypes.find(type => type.key === activeTab);

    return (
        <div className="container-fluid p-4">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-1">
                                <i className={`bx ${currentReportType?.icon} me-2`} style={{ color: currentReportType?.color }}></i>
                                {currentReportType?.name} Reports
                            </h4>
                            <p className="text-muted mb-0">View and manage {currentReportType?.name.toLowerCase()} reports</p>
                        </div>
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={onBack}
                        >
                            <i className="bx bx-arrow-back me-2"></i>
                            Back to Resources
                        </button>
                    </div>
                </div>
            </div>

            {/* Report Type Tabs */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-3">
                            <div className="row g-2">
                                {reportTypes.map((type) => (
                                    <div key={type.key} className="col-lg-3 col-md-4 col-sm-6">
                                        <button
                                            className={`btn w-100 text-start p-3 ${
                                                activeTab === type.key 
                                                    ? 'btn-primary' 
                                                    : 'btn-outline-secondary'
                                            }`}
                                            onClick={() => setActiveTab(type.key)}
                                        >
                                            <i className={`bx ${type.icon} me-2`}></i>
                                            <span className="fw-medium">{type.name}</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="row mb-4">
                <div className="col-md-8">
                    <div className="input-group">
                        <span className="input-group-text">
                            <i className="bx bx-search"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by patient name, ID, or attendee..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="col-md-4 text-end">
                    <span className="text-muted">
                        {filteredReports.length} report(s) found
                    </span>
                </div>
            </div>

            {/* Reports Cards */}
            <div className="row">
                <div className="col-12">
                    {loading && reports.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading PDF reports...</p>
                        </div>
                    ) : filteredReports.length > 0 ? (
                        <div className="row g-4">
                            {filteredReports.map((report) => {
                                const typeInfo = reportTypes.find(type => type.key === report.report_type) || reportTypes[0];
                                return (
                                    <div key={report.id} className="col-lg-4 col-md-6">
                                        <div className="card h-100 border-0 shadow-sm hover-shadow">
                                            <div className="card-body">
                                                <div className="d-flex align-items-start mb-3">
                                                    <div 
                                                        className="avatar-sm rounded flex-shrink-0 d-flex align-items-center justify-content-center me-3"
                                                        style={{ backgroundColor: '#dc354515' }}
                                                    >
                                                        <i className="bx bx-file-pdf" style={{ color: '#dc3545', fontSize: '24px' }}></i>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-1 line-clamp-2">{report.title}</h6>
                                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                                            <span 
                                                                className="badge"
                                                                style={{ backgroundColor: typeInfo.color, color: 'white' }}
                                                            >
                                                                <i className={`bx ${typeInfo.icon} me-1`}></i>
                                                                {typeInfo.name}
                                                            </span>
                                                            <span className={`badge ${getStatusBadge(report.status)}`}>
                                                                {report.status}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <p className="text-muted small mb-3 line-clamp-2">{report.summary}</p>
                                                
                                                <div className="row g-2 mb-3 small">
                                                    <div className="col-6">
                                                        <div className="text-muted">Patient</div>
                                                        <div className="fw-medium text-truncate">{report.patient_name}</div>
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="text-muted">Patient ID</div>
                                                        <div className="fw-medium">{report.patient_id}</div>
                                                    </div>
                                                </div>
                                                
                                                <div className="row g-2 mb-3 small">
                                                    <div className="col-6">
                                                        <div className="text-muted">Attendee</div>
                                                        <div className="fw-medium text-truncate">{report.attendee}</div>
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="text-muted">Date</div>
                                                        <div className="fw-medium">{formatDate(report.created_date)}</div>
                                                    </div>
                                                </div>
                                                
                                                <div className="row g-2 mb-3 text-center">
                                                    <div className="col-6">
                                                        <div className="small">
                                                            <i className="bx bx-download text-primary"></i>
                                                            <div className="fw-medium">{report.download_count}</div>
                                                            <div className="text-muted tiny">Downloads</div>
                                                        </div>
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="small">
                                                            <i className="bx bx-hdd text-info"></i>
                                                            <div className="fw-medium">{report.file_size}</div>
                                                            <div className="text-muted tiny">File Size</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="small text-muted mb-3">
                                                    <div>Department: {report.department}</div>
                                                </div>
                                            </div>
                                            
                                            <div className="card-footer bg-transparent border-0 pt-0">
                                                <div className="d-grid">
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => downloadReport(report)}
                                                        disabled={!report.is_available}
                                                    >
                                                        <i className="bx bx-download me-2"></i>
                                                        Download PDF
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <i className="bx bx-file-pdf display-1 text-muted"></i>
                            <h5 className="mt-3 text-muted">No PDF Reports Found</h5>
                            <p className="text-muted">
                                {searchTerm 
                                    ? `No PDF reports match your search criteria`
                                    : `No PDF-converted ${currentReportType?.name.toLowerCase()} reports are available.`
                                }
                            </p>
                            {searchTerm && (
                                <button 
                                    className="btn btn-outline-primary"
                                    onClick={() => setSearchTerm('')}
                                >
                                    Clear Search
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Infinite Scroll Loading Indicator */}
            {loading && reports.length > 0 && (
                <div className="row mt-4">
                    <div className="col-12 text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading more...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading more reports...</p>
                    </div>
                </div>
            )}

            {/* Reports Count */}
            {reports.length > 0 && (
                <div className="row mt-4">
                    <div className="col-12 text-center text-muted">
                        Showing {filteredReports.length} of {totalCount} PDF reports
                        {hasMore && !loading && (
                            <div className="mt-2">
                                <small>Scroll down to load more</small>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportsCenter;
